/* eslint-disable */

import RestX from 'highered-utils/restx';

import moment from 'moment';
import axios, { AxiosResponse } from 'axios';
import { BaseService } from './BaseService';
import SchoolService from '~/services/SchoolService';

interface AppointmentType {
    id: string;
    active: boolean;
    schoolId: number;
    title: string;
    accessibility: string;
    duration: number;
    location: string;
    category: string;
    calendarUserId: string;
    bookingCount: number;
    archived: boolean;
    start: string;
    end: string;
    description: string;
    is_online: boolean;
}

interface AppointmentTypeResponse {
    results: AppointmentType[];
    hasNextPage: boolean;
    totalCount: number;
    meta: Record<string, string>;
}

interface AppointmentTypesAndBookings {
    types: AppointmentType[];
    bookings: ExtendedEvent[];
}

interface Event {
    id: string;
    created_at: string;
    updated_at: string;
    user_id: string;
    appointment_type_id: string;
    smart_invite_id: string;
    start_time: string;
    status: string;
    calendar_user_id: string;
    end_time: string;
    event_id: string;
}

interface ExtendedEvent extends Event {
    from: string | undefined;
    to: string | undefined;
    title: string | undefined;
    duration: number | string | undefined;
    location: string | undefined;
    accessibility: string | undefined;
}

export type CalendarSystemEventAction = 'cancel';

export interface CalendarSystemEvent {
    action: CalendarSystemEventAction;
    eventId: string | undefined;
}

function isAppointmentTypeRelevant(item: AppointmentType) {
    if (item && item.start && item.end) {
        let now = new Date();
        let start = new Date(item.start);
        let end = new Date(item.end);
        if (start < now && end < now) {
            return false;
        }
    }
    return true;
}

function isAppointmentBookingRelevant(item: Event) {
    if (item && item.start_time && item.end_time) {
        let now = new Date();
        let start = new Date(item.start_time);
        let end = new Date(item.end_time);
        if (start < now && end < now) {
            return false;
        }
    }
    return true;
}

function bookingResultToItem(typeById: Record<string, AppointmentType>) {
    return (item: ExtendedEvent) => {
        const _type = typeById[item.appointment_type_id];
        let newItem = { ...item };

        newItem.from = moment(item.from).format('D MMMM YYYY HH:MM');
        newItem.to = moment(item.to).format('D MMMM YYYY HH:MM');

        if (_type) {
            newItem.title = _type.title;
            newItem.duration = _type.duration;
            newItem.location = _type.location;
            newItem.accessibility = _type.accessibility;
        } else {
            newItem.title = 'Unknown';
            newItem.duration = 'Unknown';
            newItem.location = 'Unknown';
            newItem.accessibility = 'Unknown';
        }
        return newItem;
    };
}

class CalendarService extends BaseService {
    callbacks: Array<() => void> = [];

    constructor() {
        super('calendar', false);
    }

    hasCalendar(): Promise<Boolean> {
        const schoolId = SchoolService.getCurrentSchoolId();

        return axios({
            url: this.getUrl('type', { school_id: `${schoolId}` }),
            method: 'GET',
        }).then((result: AxiosResponse<AppointmentTypeResponse>) => {
            return result.data.results.length > 0;
        });
    }

    getAppointmentTypesAndBookings(): Promise<AppointmentTypesAndBookings> {
        const schoolId = SchoolService.getCurrentSchoolId();

        return axios({
            url: this.getUrl('type', { school_id: `${schoolId}`, only_active: 'true' }),
            method: 'GET',
        }).then((result: AxiosResponse<AppointmentTypeResponse>) => {
            const data = result.data;

            const appointmentTypes = data.results.filter(isAppointmentTypeRelevant);

            const typeById = Object.fromEntries(
                appointmentTypes.map(item => [item.id, { ...item }])
            );

            return axios({
                url: this.getUrl('event'),
                method: 'GET',
            }).then((data: AxiosResponse<ExtendedEvent[]>) => {
                const appointmentBookings = data.data
                    .filter(isAppointmentBookingRelevant)
                    .map(bookingResultToItem(typeById));

                return { types: appointmentTypes, bookings: appointmentBookings };
            });
        });
    }

    cancelBooking(eventId: string): Promise<boolean | void> {
        return axios({
            url: this.getUrl('event/'.concat(eventId).concat('/cancel')),
            method: 'POST',
        })
            .then(this.isOK)
            .then(isOk => {
                if (isOk) {
                    const event = new CustomEvent('calendar', {
                        detail: {
                            action: 'cancel',
                            eventId: eventId,
                        } as CalendarSystemEvent,
                    });
                    window.dispatchEvent(event);
                }
            });
    }

    addListener(callback: () => void) {
        this.callbacks.push(callback);
        window.addEventListener('calendar', callback, false);
    }

    removeListener(callback: () => void) {
        this.callbacks = this.callbacks.filter(item => item !== callback);
        window.removeEventListener('calendar', callback, false);
    }

    removeAllListeners() {
        this.callbacks.forEach(callback => {
            window.removeEventListener('calendar', callback, false);
        });
        this.callbacks = [];
    }
}

export default CalendarService;
